//april 2 FormBuilderNavbarComponent

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, InputBase, Button, CircularProgress, Snackbar, Alert, Menu, MenuItem, Dialog } from '@mui/material';
//import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';          //  home button
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'; //  back “<” icon

import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useNavigate, useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useFormContext } from '../../contexts/FormContext'; // Use FormContext for form title management
import { useFormSaveContext } from '../../contexts/FormSaveContext'; // Correctly import the context
import { useFormPublishContext } from '../../contexts/FormPublishContext'; //  Import Publish Context
import { useFormListsDraftSync } from '../../contexts/FormListsDraftSyncContext';
import PublishingDialog from '../Dialogs/PublishingDialog';
import ConfirmReplaceDialog from '../Dialogs/ConfirmReplaceDialog';
import ShareForm from '../Share/ShareForm';

import { discardDraftOverlayForForm } from '../../utils/firestore';
import { useAuth } from '../../contexts/AuthContext';

// Styled AppBar with darker color and height adjustments
const StyledAppBar = styled(AppBar)({
  backgroundColor: '#000d1a', // Even darker color (almost black)
  color: '#FFFFFF',
  boxShadow: 'none',
  height: '48px', // Explicit height for the AppBar
  display: 'flex',
  justifyContent: 'center', // Ensure content is vertically centered
});

// Customize Toolbar to ensure vertical alignment and reduce padding
const StyledToolbar = styled(Toolbar)({
  height: '48px', // Match AppBar height
  minHeight: '48px', // Ensure minimum height is also set
  display: 'flex',
  alignItems: 'center', // Vertically center items
  padding: '0 16px', // Adjust padding to fit the reduced height
  boxSizing: 'border-box', // Ensure proper box-sizing
  justifyContent: 'space-between', // Ensure spacing between left and right content
  paddingRight: '0', // Remove any extra right padding
});

// Customize the hoverable box around the title and edit icon with a fixed width
const HoverableBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between', // Align the text on the left and the icon on the right
  padding: '4px 8px',
  borderRadius: '4px',
  cursor: 'pointer',
  width: 'auto',  // allow room for “<” icon + title
  height: '32px', // Adjusted height to fit slightly larger than the input
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)', // Light hover effect
  },
  whiteSpace: 'nowrap', // Prevent text from wrapping
  overflow: 'hidden', // Hide overflowed text
  textOverflow: 'ellipsis', // Show ellipsis for overflowed text
}));

// Customize text to fit in the reduced-height navbar
const StyledTypography = styled(Typography)({
  fontSize: '16px', // Adjust text size for the Form Builder title
  fontWeight: 'bold',
  lineHeight: '32px', // Ensure line-height matches hover box height for vertical centering
});

// Adjust icon size and remove extra padding
const StyledIconButton = styled(IconButton)({
  padding: '0', // Remove padding around the icon
  marginRight: '16px', // Add space between the icon and the text
  display: 'flex',
  alignItems: 'center', // Vertically center the icon
  height: '100%', // Ensure the button takes full height
});

// Adjust the size of the Edit Icon and Icon Button
const EditIconButton = styled(IconButton)({
  padding: '0', // Reduce padding around the icon button
  marginLeft: 'auto', // Ensure the icon is aligned to the right
  color: '#FFFFFF', // Keep the icon white
  height: '100%', // Ensure the button takes full height
  display: 'flex',
  alignItems: 'center', // Vertically center the button content
});

// Style the InputBase to have a transparent background and no borders
const StyledInputBase = styled(InputBase)({
  color: '#FFFFFF', // Keep the text color white
  fontSize: '16px',
  fontWeight: 'bold',
  lineHeight: '32px', // Ensure the input text matches the hover box height
  padding: '0',
  width: '100%', // Make the input field fill the width of the box
  '& .MuiInputBase-input': {
    padding: '0',
    backgroundColor: 'transparent', // Transparent background
    border: 'none', // No border
    outline: 'none', // No outline
  },
});

// Separate styles for Publish/Update and Save Draft buttons
const PublishButton = styled(Button)(({ theme }) => ({
  color: '#FFFFFF', // White text for Publish/Update
  backgroundColor: '#4285F4', // Google blue for Publish/Update
  borderColor: '#4285F4', // Border color for Publish/Update
  borderRadius: '6px',
  textTransform: 'none',
  fontSize: '11px',
  fontWeight: 'bold', // Bold for Publish/Update
  marginLeft: '10px',
  marginRight: '0px',
  padding: '8px 24px', // Separate padding for Publish button
  '&:hover': {
    backgroundColor: '#357ae8', // Darker blue on hover for Publish
  },
}));

const SaveDraftButton = styled(Button)(({ theme }) => ({
  color: '#FFFFFF', // Blue text for Save Draft
  backgroundColor: 'transparent', // Transparent background for Save Draft
  borderColor: '#4285F4', // Border color for Save Draft
  borderRadius: '6px',
  textTransform: 'none',
  fontSize: '11px', // Different size for Save Draft button
  fontWeight: 'bold', // Normal weight for Save Draft
  padding: '4px 16px', // Separate padding for Save Draft button
  marginLeft: '16px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)', // Transparent hover effect for Save Draft
  },
}));

const FormBuilderNavbarComponent = () => {
  const navigate = useNavigate();
  const { formId } = useParams(); // Get the formId from the URL params
  const { formTitle, updateTitle, isPublished, fetchFormDetails, formDetails } = useFormContext(); // Use FormContext to manage form title and publish status
  const { saveFormAsDraft } = useFormSaveContext(); // Correct usage here
  const { publishForm } = useFormPublishContext(); // Get publishForm function
  const { clearFormListsUnsavedWorkingCopy } = useFormListsDraftSync() || {};
  const { currentUser } = useAuth() || {};

  const [localFormTitle, setLocalFormTitle] = useState(''); // Local state to handle live editing
  const [editing, setEditing] = useState(false); // State to manage edit mode
  const [isSaving, setIsSaving] = useState(false); // State to manage save spinner
  const [saveSuccess, setSaveSuccess] = useState(false); // State to show success state
  const [isPublishing, setIsPublishing] = useState(false); // New state for publishing
  const [publishedFormId, setPublishedFormId] = useState(null);
  const [showShareForm, setShowShareForm] = useState(false);

  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [conflictTitle, setConflictTitle] = useState(null); // optional UI nicety

  const [openSnackbar, setOpenSnackbar] = useState(false); // State to handle Snackbar visibility
  const [snackbarMessage, setSnackbarMessage] = useState(''); // Snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // Severity of the Snackbar (success/error)

  const [draftMenuAnchorEl, setDraftMenuAnchorEl] = useState(null);
  const isDraftMenuOpen = Boolean(draftMenuAnchorEl);

  const handleOpenDraftMenu = useCallback((event) => {
    setDraftMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseDraftMenu = useCallback(() => {
    setDraftMenuAnchorEl(null);
  }, []);

  // ────────────────────────────────────────────────────────────────
  // Overlay presence + timestamps for HubSpot-style status row
  // ────────────────────────────────────────────────────────────────
  const rootStatus = formDetails?.status || null; // 'draft' | 'published' | etc.
  const isRootDraft = rootStatus === 'draft';

  const hasDraftOverlay =
    formDetails?.draftOverlayStatus === 'draftOverlay' ||
    formDetails?.draftOverlay?.status === 'draftOverlay';

  const formatMaybeTimestamp = (v) => {
    try {
      if (v && typeof v === 'object' && typeof v.toDate === 'function') return v.toDate(); // Firestore Timestamp
      if (v instanceof Date) return v;
      if (typeof v === 'string') {
        const d = new Date(v);
        return isNaN(d.getTime()) ? null : d;
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const formatStamp = (d) => {
    if (!d) return null;
    try {
      return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch (e) {
      return null;
    }
  };

  const publishedAt = formatMaybeTimestamp(formDetails?.publishedAt || null);
  const draftSavedAt = formatMaybeTimestamp(
    formDetails?.draftOverlaySavedAt ||
    formDetails?.draftOverlay?.savedAt ||
    null
  );

  const publishedStamp = formatStamp(publishedAt);
  const draftSavedStamp = formatStamp(draftSavedAt);

  // ────────────────────────────────────────────────────────────────
  // Step 4: single primary last-activity line (HubSpot-style)
  // Priority:
  //   1) Draft overlay exists → "Last saved (draft) • <draftSavedStamp>"
  //   2) Published exists      → "Last published • <publishedStamp>"
  //   3) Root draft exists     → "Draft (not published)"
  //   4) Otherwise             → "Not published"
  // ────────────────────────────────────────────────────────────────
  const lastActivityLine = useMemo(() => {
    if (hasDraftOverlay && draftSavedStamp) return `Last saved (draft) • ${draftSavedStamp}`;
    if (isPublished && publishedStamp) return `Last published • ${publishedStamp}`;
    if (isRootDraft) return 'Draft (not published)';
    return 'Not published';
  }, [hasDraftOverlay, draftSavedStamp, isPublished, publishedStamp, isRootDraft]);

  // Sync the local state with the FormContext value
  useEffect(() => {
    if (formId) {
      fetchFormDetails(formId); // Ensure form details are fetched from Firebase
    }
  }, [formId, fetchFormDetails]);

  useEffect(() => {
    if (formTitle) {
      setLocalFormTitle(formTitle); // Sync local state with FormContext title
    }
  }, [formTitle]);

  // Memoize navigation click handler to avoid re-renders
  const handleBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // 🆕 takes user to “/”
  const handleHomeClick = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Memoize handlers for title editing
  const handleEditClick = useCallback(() => {
    setEditing(true);
  }, []);

  // Handle live title changes locally without updating FormContext
  const handleTitleChange = useCallback((event) => {
    const value = event.target.value;
    if (value.length <= 25) {
      setLocalFormTitle(value); // Only update local state
    }
  }, []);

  // When exiting edit mode, push the updated title to FormContext
  const handleExitEditMode = useCallback(() => {
    setEditing(false);
    if (localFormTitle !== formTitle) {
      updateTitle(localFormTitle); // Push to FormContext only on exit
    }
    console.log('[FormBuilderNavbarComponent] Title edit completed:', localFormTitle);
  }, [localFormTitle, formTitle, updateTitle]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      handleExitEditMode(); // Save on "Enter" key press
    }
  }, [handleExitEditMode]);

  // Memoize form title input rendering to prevent unnecessary re-renders
  const formTitleInput = useMemo(() => (
    editing ? (
      <StyledInputBase
        value={localFormTitle} // Value from local state
        onChange={handleTitleChange}
        onBlur={handleExitEditMode}
        onKeyDown={handleKeyDown}
        autoFocus
        inputProps={{ maxLength: 25 }} // Limit input to 25 characters
      />
    ) : (
      <>
        <StyledTypography>
          {formTitle || `ID: ${formId}`} {/* Show the form ID if the title is empty */}
        </StyledTypography>
        <EditIconButton onClick={handleEditClick}>
          <EditIcon sx={{ fontSize: '16px' }} />
        </EditIconButton>
      </>
    )
  ), [editing, localFormTitle, formTitle, formId, handleTitleChange, handleExitEditMode, handleKeyDown]);

  const handleSaveDraft = useCallback(async () => {
    console.log('[FormBuilderNavbarComponent] Save Draft button clicked.');
    setIsSaving(true); // Show spinner
    setSaveSuccess(false); // Reset success state

    try {
      await saveFormAsDraft();

      // Refresh FormContext so the status row reflects the new overlaySavedAt / draftOverlayStatus
      await fetchFormDetails(formId);

      // Clear builder caches (auto/draft/published) because saveDraft changes what the builder resolves (overlay vs root)
      try {
        const keys = [
          `form_${formId}_layoutConfig__auto`,
          `form_${formId}_layoutConfig__auto_hash`,
          `form_${formId}_layoutConfig__draft`,
          `form_${formId}_layoutConfig__draft_hash`,
          `form_${formId}_layoutConfig__published`,
          `form_${formId}_layoutConfig__published_hash`,
        ];

        keys.forEach((k) => {
          try { sessionStorage.removeItem(k); } catch {}
          try { localStorage.removeItem(k); } catch {}
        });
      } catch (e) {
        // non-fatal
      }

      setSaveSuccess(true); // Show success state
      setSnackbarMessage('Form saved as draft successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true); // Open Snackbar

      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('[FormBuilderNavbarComponent] Error saving form as draft:', error);
      setSnackbarMessage('Failed to save form as draft.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true); // Open Snackbar
    } finally {
      setIsSaving(false); // Hide spinner
    }
  }, [saveFormAsDraft, fetchFormDetails, formId]);

const handlePublish = useCallback(async () => {
  console.log('[FormBuilderNavbarComponent] Publish button clicked.');
  setIsPublishing(true); // Show spinner
  setSnackbarMessage('');
  setPublishedFormId(null); // Clear any previous form ID

  try {
    await publishForm();

    // Refresh FormContext so the status row reflects the new publishedAt
    // and the overlay-clearing that publish performs.
    await fetchFormDetails(formId);

    // Clear builder caches (auto/draft/published) because publish changes root blobs.
    try {
      const keys = [
        `form_${formId}_layoutConfig__auto`,
        `form_${formId}_layoutConfig__auto_hash`,
        `form_${formId}_layoutConfig__draft`,
        `form_${formId}_layoutConfig__draft_hash`,
        `form_${formId}_layoutConfig__published`,
        `form_${formId}_layoutConfig__published_hash`,
      ];

      keys.forEach((k) => {
        try { sessionStorage.removeItem(k); } catch {}
        try { localStorage.removeItem(k); } catch {}
      });
    } catch (e) {
      // non-fatal
    }

    setSnackbarMessage('Form published successfully!');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
    setPublishedFormId(formId); // So the dialog knows it's done
  } catch (error) {
    const msg = String(error?.message || '');
    if (msg === 'CONFLICT_409') {
      // Stop the spinner dialog while we ask the user
      setIsPublishing(false);
      setConflictTitle(null);
      setShowReplaceConfirm(true);
      return;
    }

    console.error('[FormBuilderNavbarComponent] Error publishing form:', error);
    setSnackbarMessage('Failed to publish form.');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  } finally {
    // If you want the PublishingDialog to remain open until user closes it,
    // remove this line. Otherwise keep it so spinner doesn't stick.
    // setIsPublishing(false);
  }
}, [publishForm, fetchFormDetails, formId]);


const handleConfirmReplace = useCallback(async () => {
  setShowReplaceConfirm(false);
  setIsPublishing(true);            // show spinner again while retrying
  try {
    await publishForm({ replaceExisting: true });

    // Refresh FormContext so the status row reflects the new publishedAt
    // and the overlay-clearing that publish performs.
    await fetchFormDetails(formId);

    // Clear builder caches (auto/draft/published) because publish changes root blobs.
    try {
      const keys = [
        `form_${formId}_layoutConfig__auto`,
        `form_${formId}_layoutConfig__auto_hash`,
        `form_${formId}_layoutConfig__draft`,
        `form_${formId}_layoutConfig__draft_hash`,
        `form_${formId}_layoutConfig__published`,
        `form_${formId}_layoutConfig__published_hash`,
      ];

      keys.forEach((k) => {
        try { sessionStorage.removeItem(k); } catch {}
        try { localStorage.removeItem(k); } catch {}
      });
    } catch (e) {
      // non-fatal
    }

    setSnackbarMessage('Form published successfully!');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
    setPublishedFormId(formId);
  } catch (error) {
    console.error('[FormBuilderNavbarComponent] Replace failed:', error);
    setSnackbarMessage('Failed to replace existing Asset Upload Form.');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  } finally {
    // keep PublishingDialog open if you want to show success state;
    // if you prefer to auto-close after success, you can set false here.
    // setIsPublishing(false);
  }
}, [publishForm, fetchFormDetails, formId]);


  const handleDiscardDraft = useCallback(async () => {
    console.log('[FormBuilderNavbarComponent] Discard Draft clicked.');
    handleCloseDraftMenu();

    if (!formId) {
      console.warn('[FormBuilderNavbarComponent] No formId available; cannot discard draft.');
      return;
    }

    const ok = window.confirm('Discard saved draft changes? This will not change the published form.');
    if (!ok) return;

    try {
      const resp = await discardDraftOverlayForForm({
        formId,
        userId: currentUser?.uid || null,
      });

      if (resp?.status !== 'success') {
        throw new Error(resp?.message || 'Discard failed');
      }

      // Clear the builder layout cache for the current form (mode-aware keying)
      // IMPORTANT: clear BOTH sessionStorage + localStorage (safeSetItem may fall back)
      try {
        const keys = [
          `form_${formId}_layoutConfig__auto`,
          `form_${formId}_layoutConfig__auto_hash`,
          `form_${formId}_layoutConfig__draft`,
          `form_${formId}_layoutConfig__draft_hash`,
          `form_${formId}_layoutConfig__published`,
          `form_${formId}_layoutConfig__published_hash`,
        ];

        keys.forEach((k) => {
          try { sessionStorage.removeItem(k); } catch {}
          try { localStorage.removeItem(k); } catch {}
        });
      } catch (e) {
        // non-fatal
      }

      try {
        if (typeof clearFormListsUnsavedWorkingCopy === 'function') {
          clearFormListsUnsavedWorkingCopy({
            formId,
            reason: 'discard_draft_success',
          });
        }
      } catch (formListsDirtyClearError) {
        console.warn(
          '[FormBuilderNavbarComponent] Failed to clear Form Lists dirty ' +
            'state after discarding draft (non-fatal):',
          formListsDirtyClearError
        );
      }

      // Refresh FormContext so status row updates
      await fetchFormDetails(formId);

      setSnackbarMessage('Draft discarded.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (error) {
      console.error('[FormBuilderNavbarComponent] Error discarding draft overlay:', error);
      setSnackbarMessage('Failed to discard draft.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  }, [
    formId,
    currentUser,
    fetchFormDetails,
    handleCloseDraftMenu,
    clearFormListsUnsavedWorkingCopy,
  ]);

  // Close the Snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <>
    <StyledAppBar position="static" elevation={0}>
      <StyledToolbar>
        {/*  Home button (top‑left) */}
        <StyledIconButton onClick={handleHomeClick} edge="start">
          <HomeIcon fontSize="small" sx={{ color: '#FFFFFF' }} />
        </StyledIconButton>


        {/* Transparent box for visual spacing */}
        <Box sx={{ width: 2, bgcolor: 'transparent', alignSelf: 'stretch', marginRight: '12px' }} />

        {/* “<” back button – now its own element so only it gets hovered */}
        <StyledIconButton onClick={handleBackClick} sx={{ ml: 0.5, mr: 0.5 }}>
          <ChevronLeftIcon fontSize="small" sx={{ color: '#FFFFFF' }} />
        </StyledIconButton>

        {/* Title/edit box (separate hover area) */}
        <HoverableBox onClick={handleEditClick}>
          {formTitleInput}
        </HoverableBox>



{/* Status row + action buttons aligned to the far right */}
<Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', paddingRight: '0' }}>

  {/* last activity text (light, small, left of buttons) */}
  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.70)' }}>
      {lastActivityLine}
    </Typography>
  </Box>


  <SaveDraftButton onClick={handleSaveDraft} disabled={isSaving}>
    {isSaving ? (
      <>
        Saving
        <CircularProgress size={16} sx={{ color: '#FFFFFF', marginLeft: '8px' }} />
      </>
    ) : saveSuccess ? 'Saved' : 'Save Draft'}
  </SaveDraftButton>

  <PublishButton onClick={handlePublish} disabled={isPublishing}>
    {isPublishing ? (
      <>Publishing <CircularProgress size={16} sx={{ color: '#FFFFFF', marginLeft: '8px' }} /></>
    ) : isPublished ? 'Update' : 'Publish'}
  </PublishButton>

  {/* Draft actions menu (⋯) — only show when overlay exists */}
  {hasDraftOverlay ? (
    <>
      <IconButton
        onClick={handleOpenDraftMenu}
        sx={{ color: '#FFFFFF', ml: 1 }}
        size="small"
        aria-label="draft actions"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={draftMenuAnchorEl}
        open={isDraftMenuOpen}
        onClose={handleCloseDraftMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleDiscardDraft}>
          <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
          Discard draft
        </MenuItem>
      </Menu>
    </>
  ) : null}
</Box>

      </StyledToolbar>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} // Center the Snackbar horizontally
        >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledAppBar>
<ConfirmReplaceDialog
  open={showReplaceConfirm}
  currentTitle={conflictTitle}
  onCancel={() => setShowReplaceConfirm(false)}
  onConfirm={handleConfirmReplace}
/>

<Dialog
  open={showShareForm}
  onClose={() => setShowShareForm(false)}
  fullWidth
  maxWidth="sm"
>
  <Box sx={{ p: 3 }}>
    <ShareForm
      formName={formTitle}
      formId={formId}
      formUrl={isPublished ? `/forms/${formId}` : null}
      formStatus={isPublished ? 'published' : (formDetails?.status || 'draft')}
      isPublic={formDetails?.isPublic}
      orgAccess={formDetails?.orgAccess}
      sharedWith={formDetails?.sharedWith}
      onClose={() => setShowShareForm(false)}
    />
  </Box>
</Dialog>

  <PublishingDialog
    open={isPublishing}
    publishedFormId={publishedFormId}
    vanitySlug={formDetails?.vanitySlug || null}
    onShare={() => setShowShareForm(true)}
    onClose={() => {
      setIsPublishing(false);
      setPublishedFormId(null);
    }}
  />

  </>

  );
};

export default FormBuilderNavbarComponent;
